import { useMemo, useState, useEffect, useCallback } from "react";
import { useResponsive } from "../../../../hooks/useResponsive";
import MobileKanban from "../../../board/components/Kanban/mobileKanban";
import KanbanBoard from "./KanbanBoard";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { debounce } from "lodash";

function Kanban({ workflowSteps, cards, callback, isMyTaskView }) {
  const isMobile = useResponsive("down", "md");
  const [filteredCards, setFilteredCards] = useState(cards);
  const [searchParams] = useSearchParams();

  // Memoize search params to prevent unnecessary re-renders
  const searchParamsData = useMemo(
    () => ({
      filterUser: searchParams.get("search")?.split(",") || [],
      noMileStoneSearch: searchParams.get("noMilestone") === "true",
      cardId: searchParams.get("card_id"),
    }),
    [searchParams]
  );

  // Redux state
  const searchText = useSelector((state) => state.search.searchText);
  const epicSearch = useSelector((state) => state.epic.searchEpic);

  // Memoize the filter functions
  const filterFunctions = useMemo(
    () => ({
      searchText: (cards) => {
        if (!searchText) return cards;
        return cards?.filter(
          (cardData) => {
            const normalizedSearchText = searchText.toLowerCase();
            const boardCardKey = `${cardData?.key || ""}-${cardData?.card?.card_id || ""}`.toLowerCase();

            return (
              cardData?.card?.title
                ?.toLowerCase()
                .includes(normalizedSearchText) ||
              cardData?.card?.assigned_to_user?.first_name
                ?.toLowerCase()
                .includes(normalizedSearchText) ||
              cardData?.card?.assigned_to_user?.last_name
                ?.toLowerCase()
                .includes(normalizedSearchText) ||
              cardData?.key?.toLowerCase().includes(normalizedSearchText) ||
              cardData?.card?.card_id
                ?.toString()
                .toLowerCase()
                .includes(normalizedSearchText) ||
              boardCardKey.includes(normalizedSearchText) ||
              cardData?.card?.epic?.epic_name
                ?.toLowerCase()
                .includes(normalizedSearchText) ||
              cardData?.labels?.some((ele) =>
                ele?.toLowerCase().includes(normalizedSearchText)
              )
            );
          }
        );
      },
      user: (cards) => {
        if (!searchParamsData.filterUser.length) return cards;
        return cards?.filter((el) =>
          searchParamsData.filterUser.includes(
            String(el?.card?.assigned_to_user_id)
          )
        );
      },
      epic: (cards) => {
        if (!epicSearch?.length) return cards;
        return cards?.filter((item) =>
          epicSearch.includes(item?.card?.epic_id)
        );
      },
      milestone: (cards) => {
        if (!searchParamsData.noMileStoneSearch) return cards;
        return cards?.filter((item) => item?.card?.epic_id == null);
      },
    }),
    [searchText, searchParamsData, epicSearch]
  );

  // Memoize the debounced filter function
  const debouncedFilter = useMemo(
    () =>
      debounce((originalCards) => {
        let result = [...originalCards];
        result = filterFunctions.user(result);
        result = filterFunctions.epic(result);
        result = filterFunctions.milestone(result);
        result = filterFunctions.searchText(result);
        setFilteredCards(result);
      }, 300),
    [filterFunctions]
  );

  // Apply filters when dependencies change
  useEffect(() => {
    if (!cards) return;
    debouncedFilter(cards);

    return () => {
      debouncedFilter.cancel();
    };
  }, [cards, debouncedFilter]);

  return (
    <div>
      {isMobile ? (
        <MobileKanban
          workflow={workflowSteps}
          cards={filteredCards}
          isMyTaskView={isMyTaskView}
        />
      ) : (
        <KanbanBoard
          workflowSteps={workflowSteps}
          cards={cards}
          callback={callback}
        />
      )}
    </div>
  );
}

export default Kanban;
